import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  InputGroup,
  Row,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Flatpickr from "react-flatpickr";

import { jobGrid } from "../../../common/data/appsJobs";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import JobListingService from "../../../services/KangarooServices/JobListingService";
import { formatDate } from "../../../Components/Common/FormatDate";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";

const JobGrid = () => {
  const navigate = useNavigate();
  const [jobData, setJobData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [filters, setFilters] = useState({ search: "" });

  const perPageData = 6; // show 6 cards per page

  const handleSearchChange = (e) =>
    setFilters((prev) => ({ ...prev, search: e.target.value }));

  const toggleDelete = () => setIsOpenDelete(!isOpenDelete);

  const handleDeleteClick = (item) => {
    setSelectedDeleteItem(item);
    toggleDelete();
  };

  const handleDelete = async () => {
    if (selectedDeleteItem) {
      try {
        await JobListingService.delete(selectedDeleteItem.id);
        const response = await JobListingService.getList();
        setJobData(response);
        toast.success("Job deleted successfully");
      } catch (error) {
        toast.error(error?.response?.data?.message || "Failed to delete Job");
      } finally {
        toggleDelete();
        setSelectedDeleteItem(null);
      }
    }
  };

  // Pagination: calculate current page items
  const filteredJobs = useMemo(() => {
    if (!filters.search) return jobData?.items || [];
    return jobData.items.filter((job) =>
      job.position.toLowerCase().includes(filters.search.toLowerCase())
    );
  }, [jobData, filters.search]);

  const indexOfLast = currentPage * perPageData;
  const indexOfFirst = indexOfLast - perPageData;
  const currentJobs = filteredJobs.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredJobs.length / perPageData);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await JobListingService.getList();
      setJobData(response);
    };
    fetchData();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadCrumb title="Job Listing" pageTitle="Content Management" />

          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center ">
                <button
                  className="btn btn-success"
                  onClick={() => navigate("/joblisting/create")}
                >
                  <i className="bi bi-plus-lg"></i> Add Job Listing
                </button>
              </div>
            </Col>
            <Col
              className="d-flex justify-content-end align-items-end mb-2"
              md={6}
            >
              <InputGroup className="w-auto">
                <Input
                  placeholder="Search..."
                  value={filters.search}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </Col>
          </Row>

          <Row className="row g-4" id="job-list">
            {currentJobs.map((item, key) => (
              <Col lg={4} md={6} key={key}>
                <Card className="border shadow-sm rounded-4 h-100 overflow-hidden position-relative ">
                  <CardBody className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <Badge
                        pill
                        className={`px-3 py-2 ${
                          item?.status === 0
                            ? "bg-danger-subtle text-danger"
                            : "bg-success-subtle text-success"
                        }`}
                      >
                        <i
                          className={`ri-checkbox-blank-circle-fill me-1 small`}
                        ></i>
                        {item?.status === 0 ? "Closed" : "Active"}
                      </Badge>
                      <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-2">
                        <i className="ri-briefcase-4-fill fs-4"></i>
                      </div>
                    </div>

                    <div className="mb-4">
                      <Link to="#!" className="text-decoration-none">
                        <h5 className="fw-bold text-dark mb-1 text-truncate">
                          {item?.position}
                        </h5>
                      </Link>
                      <div className="d-flex align-items-center text-primary">
                        <small className="fw-medium bg-light px-2 py-1 rounded">
                          <i className="ri-team-line me-1"></i>
                          {item?.slotsLeft} Slots Remaining
                        </small>
                      </div>
                    </div>

                    <div className="bg-light rounded-3 p-3 mb-4">
                      <div className="d-flex align-items-center mb-2">
                        <i className="ri-calendar-event-line text-muted me-2"></i>
                        <div>
                          <p className="text-muted small mb-0">Posted on</p>
                          <p className="fw-semibold small mb-0">
                            {formatDate(item?.publishedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <i className="ri-timer-flash-line text-muted me-2"></i>
                        <div>
                          <p className="text-muted small mb-0">Deadline</p>
                          <p className="fw-semibold small mb-0">
                            {formatDate(item?.deadline)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-center bg-warning bg-opacity-10 border border-warning border-opacity-25 rounded-3 p-2 mb-4">
                      <i className="ri-alarm-warning-fill text-warning me-2"></i>
                      <span className="text-warning-emphasis fw-bold small">
                        Days Left: {item?.daysLeft}
                      </span>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-auto">
                      <Link
                        to={`/joblisting/detail/${item?.id}`}
                        className="card-link link-secondary text-decoration-none fw-semibold d-flex align-items-center"
                      >
                        View Details
                        <i className="ri-arrow-right-s-line ms-1 align-middle lh-1"></i>
                      </Link>
                      <div>
                        <Button
                          size="sm"
                          color="primary"
                          className="me-2 rounded-pill"
                          onClick={() =>
                            navigate(`/joblisting/update/${item?.id}`)
                          }
                        >
                          <i className="bx bx-edit-alt"></i>
                        </Button>

                        <Button
                          size="sm"
                          color="danger"
                          className="rounded-pill"
                          onClick={() => handleDeleteClick(item)}
                        >
                          <i className="ri-delete-bin-5-line"></i>
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          {/* Reactstrap Pagination */}
          {totalPages > 1 && (
            <Row className="mt-4">
              <Col>
                <Pagination className="justify-content-center">
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink first onClick={() => handlePageChange(1)} />
                  </PaginationItem>
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      previous
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                  </PaginationItem>

                  {[...Array(totalPages)].map((_, index) => (
                    <PaginationItem
                      active={currentPage === index + 1}
                      key={index}
                    >
                      <PaginationLink
                        onClick={() => handlePageChange(index + 1)}
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}

                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      next
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </PaginationItem>
                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      last
                      onClick={() => handlePageChange(totalPages)}
                    />
                  </PaginationItem>
                </Pagination>
              </Col>
            </Row>
          )}
        </div>
      </div>

      <DeleteModal
        show={isOpenDelete}
        onCloseClick={toggleDelete}
        onDeleteClick={handleDelete}
      />
    </React.Fragment>
  );
};

export default JobGrid;
